// src/components/home/PerformanceCard.tsx
//
// Consolidated "Are we winning or falling behind?" performance surface.
// Merges the old Workspace Health scorecard + This Week + Active Goals +
// Project Progress into one card: a deterministic KPI header plus expandable
// detail (Goals, Projects, Team / This Week). No AI in the data — the optional
// AI summary is a de-emphasized link.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useGetManagementSummaryQuery } from '../../api/managementApi';
import { useGetGoalsQuery } from '../../api/goalsApi';
import { useGetProjectsQuery } from '../../api/projectsApi';
import { computeProjectHealth } from '../../services/projectHealth';
import { TrackingBadge, ProgressBar } from '../projects/progressUi';

function Section({ title, defaultOpen = false, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-gray-100 pt-3">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && children}
    </div>
  );
}

function Kpi({ value, label, tone }: { value: string | number; label: string; tone?: 'bad' | 'warn' | 'ok' }) {
  const cls = tone === 'bad' ? 'text-red-600' : tone === 'warn' ? 'text-amber-600' : 'text-gray-900';
  return (
    <div className="p-2 rounded-lg bg-gray-50 text-center">
      <p className={`text-lg font-bold leading-none ${cls}`}>{value}</p>
      <p className="text-[11px] text-gray-500 mt-1">{label}</p>
    </div>
  );
}

interface PerformanceCardProps {
  workspaceId: string;
  today?: string;
  onAskAI?: () => void;
  isAskingAI?: boolean;
  allowAI?: boolean;
}

export default function PerformanceCard({ workspaceId, today, onAskAI, isAskingAI, allowAI }: PerformanceCardProps) {
  const navigate = useNavigate();
  const day = today ?? new Date().toISOString().split('T')[0];

  const { data: managementSummary } = useGetManagementSummaryQuery(undefined, { skip: !workspaceId });
  const { data: goalsData } = useGetGoalsQuery({ workspaceId, status: 'active', limit: 20 }, { skip: !workspaceId });
  const { data: projects } = useGetProjectsQuery({ workspaceId }, { skip: !workspaceId });

  const goals = useMemo(() => goalsData?.goals ?? [], [goalsData]);
  const activeProjects = useMemo(
    () => (projects ?? []).filter((p) => p.status !== 'completed' && p.status !== 'cancelled'),
    [projects],
  );

  const kpis = useMemo(() => {
    const completionRate = managementSummary ? Math.round(managementSummary.progress.completionRate * 100) : null;
    const overdue = managementSummary?.gaps.overdueTasks ?? 0;
    const blocked = managementSummary?.gaps.blockedTasks ?? 0;
    const goalsBehind = goals.filter((g) => g.progress?.trackingStatus === 'behind').length;
    const projectsAtRisk = activeProjects.filter((p) => computeProjectHealth(p, { today: day }).health !== 'healthy').length;
    const capacity = managementSummary?.capacity ?? [];
    const overloaded = capacity.filter((m) => m.openTaskCount > 10 || m.estimatedMinutes > 480).length;
    return { completionRate, overdue, blocked, goalsBehind, projectsAtRisk, overloaded };
  }, [managementSummary, goals, activeProjects, day]);

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">Performance</h2>

      {/* KPI header — "are we winning or falling behind?" */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        <Kpi value={kpis.completionRate == null ? '—' : `${kpis.completionRate}%`} label="This week" />
        <Kpi value={kpis.overdue} label="Overdue" tone={kpis.overdue > 0 ? 'bad' : undefined} />
        <Kpi value={kpis.blocked} label="Blocked" tone={kpis.blocked > 0 ? 'warn' : undefined} />
        <Kpi value={kpis.goalsBehind} label="Goals behind" tone={kpis.goalsBehind > 0 ? 'bad' : undefined} />
        <Kpi value={kpis.projectsAtRisk} label="Projects at risk" tone={kpis.projectsAtRisk > 0 ? 'bad' : undefined} />
        <Kpi value={kpis.overloaded} label="Overloaded" tone={kpis.overloaded > 0 ? 'warn' : undefined} />
      </div>

      {/* Detail: Goals */}
      <Section title={`Goals (${goals.length})`}>
        {goals.length === 0 ? (
          <p className="text-sm text-gray-500">No active goals.</p>
        ) : (
          <ul className="space-y-2">
            {goals.slice(0, 8).map((g) => (
              <li key={g.id}>
                <button onClick={() => navigate('/goals')} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 text-left">
                  <span className="flex-1 min-w-0 text-sm text-gray-900 truncate">
                    {g.title}
                    {g.executionSummary && g.executionSummary.overdueTasks > 0 && (
                      <span className="text-xs text-red-500"> · {g.executionSummary.overdueTasks} overdue</span>
                    )}
                  </span>
                  {g.progress?.progressPercent != null && (
                    <span className="text-xs text-gray-500 flex-shrink-0">{g.progress.progressPercent.toFixed(0)}%</span>
                  )}
                  {g.progress && <TrackingBadge status={g.progress.trackingStatus} />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Detail: Projects */}
      <Section title={`Projects (${activeProjects.length})`}>
        {activeProjects.length === 0 ? (
          <p className="text-sm text-gray-500">No active projects.</p>
        ) : (
          <ul className="space-y-2">
            {activeProjects.slice(0, 8).map((p) => (
              <li key={p.id}>
                <button onClick={() => navigate(`/projects?projectId=${p.id}`)} className="w-full text-left p-2 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm text-gray-900 truncate">{p.name}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {p.progress?.progressPercent != null && <span className="text-xs text-gray-500">{p.progress.progressPercent.toFixed(0)}%</span>}
                      {p.progress && <TrackingBadge status={p.progress.trackingStatus} />}
                    </div>
                  </div>
                  {p.progress && <ProgressBar percent={p.progress.progressPercent} status={p.progress.trackingStatus} />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Detail: Team / This Week */}
      <Section title="Team / This Week">
        {!managementSummary ? (
          <p className="text-sm text-gray-500">No team data.</p>
        ) : (
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-500">Weekly progress</span>
                <button onClick={() => navigate('/tasks')} className="text-sm font-medium text-blue-600 hover:underline">
                  {managementSummary.progress.completedThisWeek} / {managementSummary.progress.totalTasksThisWeek} tasks
                </button>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${Math.round(managementSummary.progress.completionRate * 100)}%` }} />
              </div>
            </div>
            {managementSummary.capacity.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-500">Team capacity</span>
                  <button onClick={() => navigate('/team')} className="text-xs text-blue-600 hover:underline">View team</button>
                </div>
                <ul className="space-y-1">
                  {managementSummary.capacity.slice(0, 4).map((m) => (
                    <li key={m.userId} className="flex items-center justify-between text-sm text-gray-700">
                      <span className="truncate">{m.openTaskCount} open</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {m.estimatedMinutes > 0 ? `~${Math.round(m.estimatedMinutes / 60)}h` : m.unestimatedTaskCount > 0 ? `${m.unestimatedTaskCount} unestimated` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Section>

      {/* De-emphasized AI summary */}
      {allowAI && onAskAI && (
        <div className="pt-1">
          <button onClick={onAskAI} disabled={isAskingAI} className="text-xs text-gray-500 hover:text-gray-700 hover:underline disabled:opacity-60">
            {isAskingAI ? 'Opening AI…' : 'Ask AI to summarize workspace health →'}
          </button>
        </div>
      )}
    </div>
  );
}
